import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import ClientContacts from './ClientContacts';
import ClientProjectPlan from './ClientProjectPlan';
import ClientProjectsTab from './ClientProjectsTab';
import ClientInfoTab from './ClientInfoTab';
import ClientProductsTab from './ClientProductsTab';
import ClientRevenueTab from './ClientRevenueTab';
import ClientEmployeeTab from './ClientEmployeeTab';

const ClientDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('info');
  const [formData, setFormData] = useState({
    name: '',
    folderLink: '',
    is_active: true,
    is_archived: false
  });
  
  useEffect(() => {
    const fetchClientDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await api.get(`/clients/${id}`);
        console.log('Client data response:', response.data);
        
        // Extract client data from response
        // The server returns { client: { ... } } format
        const clientData = response.data.client || response.data;
        
        console.log('Projects in client data:', clientData.projects);
        
        setClient(clientData);
        // Initialize form data with client data for editing
        setFormData({
          name: clientData.name || '',
          folderLink: clientData.folderLink || '',
          is_active: clientData.is_active !== undefined ? clientData.is_active : true,
          is_archived: clientData.is_archived !== undefined ? clientData.is_archived : false
        });
      } catch (err) {
        console.error('Error fetching client details:', err);
        setError('Failed to load client details. The client may not exist or the server is unavailable.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchClientDetails();
  }, [id]);
  
  // Callback function to handle client updates from child components
  const handleClientUpdate = (updatedClient) => {
    setClient(updatedClient);
    // Also update form data to keep it in sync
    setFormData({
      name: updatedClient.name || '',
      folderLink: updatedClient.folderLink || '',
      is_active: updatedClient.is_active !== undefined ? updatedClient.is_active : true,
      is_archived: updatedClient.is_archived !== undefined ? updatedClient.is_archived : false
    });
  };
  
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="w-12 h-12 border-t-2 border-b-2 border-gray-500 rounded-full animate-spin"></div>
        <p className="mt-4 text-gray-600">Loading client details...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="p-4 text-red-700 bg-red-100 rounded-md">
        <p>{error}</p>
        <div className="mt-4">
          <Link
            to="/clients"
            className="text-red-700 underline"
          >
            Return to Client List
          </Link>
        </div>
      </div>
    );
  }
  
  if (!client) {
    return (
      <div className="p-4 text-gray-700 bg-gray-100 rounded-md">
        <p>Client not found.</p>
        <div className="mt-4">
          <Link
            to="/clients"
            className="text-indigo-600 hover:text-indigo-900"
          >
            Return to Client List
          </Link>
        </div>
      </div>
    );
  }
  
  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">
          {client.name}
          <>
            {client.is_archived && (
              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium bg-gray-100 text-gray-800">
                Archived
              </span>
            )}
            {!client.is_active && !client.is_archived && (
              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium bg-yellow-100 text-yellow-800">
                Inactive
              </span>
            )}
          </>
        </h1>
        <div className="flex space-x-3 items-center">
          <Link
            to="/clients"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Back to Clients
          </Link>
        </div>
      </div>
      
      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mt-4">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('info')}
            className={`${
              activeTab === 'info'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Company Information
          </button>
          <button
            onClick={() => setActiveTab('contacts')}
            className={`${
              activeTab === 'contacts'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Contacts
          </button>
          <button
            onClick={() => setActiveTab('project-plan')}
            className={`${
              activeTab === 'project-plan'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Project Plan
          </button>
          <button
            onClick={() => setActiveTab('projects')}
            className={`${
              activeTab === 'projects'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Projects
          </button>
          <button
            onClick={() => setActiveTab('products')}
            className={`${
              activeTab === 'products'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Products
          </button>
          <button
            onClick={() => setActiveTab('revenue')}
            className={`${
              activeTab === 'revenue'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Revenue
          </button>
          <button
            onClick={() => setActiveTab('employees')}
            className={`${
              activeTab === 'employees'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Employees
          </button>
        </nav>
      </div>
      
      {/* Client Info Tab */}
      {activeTab === 'info' && (
        <ClientInfoTab client={client} onClientUpdate={handleClientUpdate} />
      )}
      
      {/* Contacts Tab */}
      {activeTab === 'contacts' && (
        <ClientContacts clientId={id} />
      )}
      
      {/* Project Plan Tab */}
      {activeTab === 'project-plan' && (
        <ClientProjectPlan clientId={id} clientName={client.name} />
      )}
      
      {/* Projects Tab */}
      {activeTab === 'projects' && (
        <ClientProjectsTab client={client} />
      )}
      
      {/* Products Tab */}
      {activeTab === 'products' && (
        <ClientProductsTab client={client} />
      )}
      
      {/* Revenue Tab */}
      {activeTab === 'revenue' && (
        <ClientRevenueTab client={client} />
      )}
      
      {/* Employees Tab */}
      {activeTab === 'employees' && (
        <ClientEmployeeTab client={client} />
      )}
    </div>
  );
};

export default ClientDetails; 